import useClickAwayListener from 'hooks/useClickAwayListener'
import { useRef, useState, useMemo, FC, useEffect } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled, { useTheme } from 'styled-components'
import { width } from 'styled-system'
import { SearchIconNewDefault } from '../atoms/icons/SearchIconDefaultNew'
import { ClearIconDefault } from '../atoms/icons/ClearIconDefault'
import SearchInfoIcon from '@/components/atoms/svg/SearchInfoIcon'
import ArrowRightIcon from '@/components/atoms/svg/ArrowRightIcon'
import { FilterSection } from '@/network/graphql.g'
import Link from '../atoms/link'
import { validFor } from '@/theme/validFor'

type SearchDropdownProps = {
  placeholder: string
  value?: string
  onChange?: (value: string) => void
  onClear?: () => void
  manufacturers?: FilterSection[]
  getManufacturerUrl?: (id: string) => string
  isMobile?: boolean
} & Rest

const SearchDropdown: FC<SearchDropdownProps> = ({
  placeholder,
  value = '',
  onChange,
  onClear,
  manufacturers,
  getManufacturerUrl = (id) => `/manufacturer/${id}`,
  isMobile = false,
  ...rest
}) => {
  const theme = useTheme()
  const [opened, setOpened] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>(value)
  const [showLocalOverlay, setShowLocalOverlay] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useClickAwayListener(wrapperRef, () => {
    setOpened(false)
    setShowLocalOverlay(false)
  })

  const handleClear = () => {
    setSearchValue('')
    setOpened(false)
    setShowLocalOverlay(false)
    onClear?.()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchValue(newValue)
    onChange?.(newValue)
    if (newValue) {
      setOpened(true)
      setShowLocalOverlay(true)
    } else {
      setShowLocalOverlay(false)
    }
  }

  const handleInputFocus = () => {
    setOpened(true)
    setShowLocalOverlay(true)
  }

  const handleManufacturerClick = () => {
    setOpened(false)
    setShowLocalOverlay(false)
    setSearchValue('')
  }

  const handleOverlayClick = () => {
    setOpened(false)
    setShowLocalOverlay(false)
  }

  const filteredManufacturers = useMemo(() => {
    if (!manufacturers) {
      return []
    }

    const allManufacturers = manufacturers.flatMap(
      (section) => section.items || []
    )
    if (searchValue.length === 0) {
      return []
    }

    return allManufacturers.filter(({ title }) =>
      title.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [manufacturers, searchValue])

  // Close dropdown when search is cleared
  useEffect(() => {
    if (!searchValue) {
      setOpened(false)
      setShowLocalOverlay(false)
    }
  }, [searchValue])

  return (
    <>
      {/* Local overlay only for this component */}
      {!isMobile && showLocalOverlay && (
        <LocalOverlay onClick={handleOverlayClick} />
      )}

      <Wrapper ref={wrapperRef} showOverlay={showLocalOverlay} {...rest}>
        <SearchContainer showOverlay={showLocalOverlay}>
          <IconWrapper left>
            <SearchIconNewDefault
              size={20}
              color={theme.colors.inputPlaceholder}
            />
          </IconWrapper>

          <StyledInput
            type="text"
            placeholder={placeholder}
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            data-testid="searchInputManufacturer"
          />

          {searchValue && (
            <IconWrapper right onClick={handleClear}>
              <ClearIconDefault />
            </IconWrapper>
          )}
        </SearchContainer>

        {opened && searchValue && filteredManufacturers.length > 0 && (
          <DropdownContent showOverlay={showLocalOverlay} isMobile={isMobile}>
            <StyledShadow />
            <ManufacturersList>
              {filteredManufacturers.map((manufacturer, index) => (
                <div key={manufacturer.id}>
                  <Link
                    href={getManufacturerUrl(manufacturer.id)}
                    passHref
                    legacyBehavior
                  >
                    <ListItem isMobile={isMobile}>
                      <LeftIconWrapper>
                        <SearchInfoIcon color="#8f8f8f" />
                      </LeftIconWrapper>

                      <ManufacturerLink onClick={handleManufacturerClick}>
                        <ManufacturerName>
                          {manufacturer.title}
                        </ManufacturerName>
                        {manufacturer.count && (
                          <ItemCount>({manufacturer.count})</ItemCount>
                        )}
                      </ManufacturerLink>

                      <RightIconWrapper>
                        <ArrowRightIcon color="#000" />
                      </RightIconWrapper>
                    </ListItem>
                  </Link>
                  {index < filteredManufacturers.length - 1 && (
                    <Divider isMobile={isMobile} />
                  )}
                </div>
              ))}
            </ManufacturersList>
          </DropdownContent>
        )}

        {/* {opened &&
          manufacturers &&
          searchValue &&
          filteredManufacturers.length === 0 && (
            <DropdownContent showOverlay={showLocalOverlay} isMobile={isMobile}>
              <NoResultsMessage>No manufacturers found</NoResultsMessage>
            </DropdownContent>
          )} */}
      </Wrapper>
    </>
  )
}

export default SearchDropdown

const LocalOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.4);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99;
  transition: opacity 0.3s ease-out;
  pointer-events: auto;
`

const Wrapper = styled(Box)<{ showOverlay?: boolean }>`
  ${width}
  position: relative;
  z-index: ${(props) => (props.showOverlay ? 100 : 1)};
`

const SearchContainer = styled(Flex)<{ showOverlay?: boolean }>`
  position: relative;
  width: 100%;
  background: ${(props) =>
    props.showOverlay ? props.theme.colors.background : '#eeeeee'};
  border-radius: 8px;
  align-items: center;
  transition: all 0.2s ease;
  overflow: hidden;
  margin: 20px 0;

  &:hover {
    border-color: ${(props) => props.theme.colors.borderHover || '#D0D0D0'};
  }

  &:focus-within {
    border-color: ${(props) => props.theme.colors.primary || '#000'};
  }

  ${validFor.mobile`
      width: auto;
      margin: 20px 5px;
      font-size: 20px;
    `}
`

const IconWrapper = styled(Flex)<{ left?: boolean; right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => props.left && 'left: 10px;'}
  ${(props) => props.right && 'right: 10px;'}
  width: 36px;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.right ? 'pointer' : 'default')};
  z-index: 1;
`

const StyledInput = styled.input`
  width: 100%;
  padding: 8px;
  padding: 14px 48px 14px 55px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: ${(props) => props.theme.colors.text};
  outline: none;

  &::placeholder {
    color: #757575;
  }
`

const DropdownContent = styled(Box)<{
  showOverlay?: boolean
  isMobile?: boolean
}>`
  position: absolute;
  top: calc(100% + 25px);
  left: 0;
  right: 0;
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border || '#E5E5E5'};

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: ${(props) => (props.showOverlay ? 101 : 10)};
  overflow-y: auto;
  padding: 1px 0px 10px 0px;

  ${validFor.mobile`
      left: -10px;
    right: -10px;
   height: calc(100vh - 100px);
  
  `}
`

const StyledShadow = styled.div`
  height: 5px;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(221, 221, 221, 0.4), transparent);
`

const ManufacturersList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const ListItem = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  ${(props) => (props.isMobile ? `padding: 10px 0;` : `margin-top: 10px;`)}
`

const LeftIconWrapper = styled.div`
  padding: 0px 0px 0px 20px;
`

const RightIconWrapper = styled.div`
  margin-left: auto;
  padding: 0px 20px 0px 0px;
`

const ManufacturerLink = styled.a`
  display: flex;
  align-items: center;
  flex: 1;
  margin-left: 20px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`

const ManufacturerName = styled.span`
  font-size: 12px;
  color: #757575;
  font-weight: 400;
`

const ItemCount = styled.span`
  font-size: 12px;
  color: #757575;
  margin-left: 8px;
`

const Divider = styled.hr<{ isMobile?: boolean }>`
  width: 100%;
  border: 0;
  margin: 0;
  border-top: 1px solid #eeeeee;
`

// const NoResultsMessage = styled.div`
//   padding: 16px;
//   text-align: center;
//   color: ${(props) => props.theme.colors.textSecondary || '#666'};
//   font-size: 14px;
// `
